import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  getAuthorshipById,
  getAuthorships,
  getAuthorshipsByInjunctionId,
  getAuthorshipsByImpositionId,
  getAuthorshipsByPeriodId,
  updateAuthorship,
  type AuthorshipEvidenceModel,
  type AuthorshipModel,
  type PaginatedResponse,
  type UpdateAuthorshipParams,
} from "@/api/cle-api";

import {
  mergeEvidenceDetailUpdate,
  reviewThreadAuditTrailQueryKey,
} from "./useReviewThread";

export type { UpdateAuthorshipParams } from "@/api/cle-api";

export interface UseAuthorshipsParams {
  userId?: string;
  page?: number;
  pageSize?: number;
  reported?: boolean;
}

export interface UseAuthorshipParams {
  userId?: string;
  authorshipId?: string;
}

export interface UseAuthorshipsByPeriodParams {
  userId?: string;
  periodId?: string;
}

export interface UseAuthorshipsByInjunctionParams {
  userId?: string;
  injunctionId?: string;
}

export interface UseAuthorshipsByImpositionParams {
  userId?: string;
  impositionId?: string;
}

export const authorshipsQueryKey = ({
  userId,
  page = 1,
  pageSize = 20,
  reported,
}: UseAuthorshipsParams) =>
  [
    "cle-api",
    "users",
    userId,
    "authorships",
    page,
    pageSize,
    reported,
  ] as const;

export const authorshipsByPeriodQueryKey = ({
  userId,
  periodId,
}: UseAuthorshipsByPeriodParams) =>
  ["cle-api", "users", userId, "authorships", "by-period", periodId] as const;

export const authorshipsByInjunctionQueryKey = ({
  userId,
  injunctionId,
}: UseAuthorshipsByInjunctionParams) =>
  [
    "cle-api",
    "users",
    userId,
    "authorships",
    "by-injunction",
    injunctionId,
  ] as const;

export const authorshipsByImpositionQueryKey = ({
  userId,
  impositionId,
}: UseAuthorshipsByImpositionParams) =>
  [
    "cle-api",
    "users",
    userId,
    "authorships",
    "by-imposition",
    impositionId,
  ] as const;

export const authorshipQueryKey = ({
  userId,
  authorshipId,
}: UseAuthorshipParams) =>
  ["cle-api", "users", userId, "authorships", authorshipId] as const;

export const useAuthorships = ({
  userId,
  page = 1,
  pageSize = 20,
  reported,
}: UseAuthorshipsParams): UseQueryResult<
  PaginatedResponse<AuthorshipModel>,
  Error
> =>
  useQuery({
    queryKey: authorshipsQueryKey({ userId, page, pageSize, reported }),
    queryFn: () =>
      getAuthorships({
        userId: userId ?? "",
        page,
        pageSize,
        reported,
      }),
    enabled: !!userId,
  });

export const useAuthorshipsByPeriod = ({
  userId,
  periodId,
}: UseAuthorshipsByPeriodParams): UseQueryResult<AuthorshipModel[], Error> =>
  useQuery({
    queryKey: authorshipsByPeriodQueryKey({ userId, periodId }),
    queryFn: () =>
      getAuthorshipsByPeriodId({
        userId: userId ?? "",
        periodId: periodId ?? "",
      }),
    enabled: !!userId && !!periodId,
  });

export const useAuthorshipsByInjunction = ({
  userId,
  injunctionId,
}: UseAuthorshipsByInjunctionParams): UseQueryResult<
  AuthorshipModel[],
  Error
> =>
  useQuery({
    queryKey: authorshipsByInjunctionQueryKey({ userId, injunctionId }),
    queryFn: () =>
      getAuthorshipsByInjunctionId({
        userId: userId ?? "",
        injunctionId: injunctionId ?? "",
      }),
    enabled: !!userId && !!injunctionId,
  });

export const useAuthorshipsByImposition = ({
  userId,
  impositionId,
}: UseAuthorshipsByImpositionParams): UseQueryResult<
  AuthorshipModel[],
  Error
> =>
  useQuery({
    queryKey: authorshipsByImpositionQueryKey({ userId, impositionId }),
    queryFn: () =>
      getAuthorshipsByImpositionId({
        userId: userId ?? "",
        impositionId: impositionId ?? "",
      }),
    enabled: !!userId && !!impositionId,
  });

export const useAuthorship = ({
  userId,
  authorshipId,
}: UseAuthorshipParams): UseQueryResult<AuthorshipEvidenceModel, Error> =>
  useQuery({
    queryKey: authorshipQueryKey({ userId, authorshipId }),
    queryFn: () =>
      getAuthorshipById({
        userId: userId ?? "",
        authorshipId: authorshipId ?? "",
      }),
    enabled: !!userId && !!authorshipId,
  });

export const useUpdateAuthorship = (): UseMutationResult<
  AuthorshipEvidenceModel,
  Error,
  UpdateAuthorshipParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAuthorship,
    onSuccess: (data, { authorshipId, userId }) => {
      queryClient.setQueryData<AuthorshipEvidenceModel>(
        authorshipQueryKey({ userId, authorshipId }),
        (old) => mergeEvidenceDetailUpdate(old, data),
      );
      void queryClient.invalidateQueries({
        queryKey: reviewThreadAuditTrailQueryKey(authorshipId),
      });
      void queryClient.invalidateQueries({
        queryKey: ["cle-api", "users", userId, "authorships"],
      });
    },
  });
};
